import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { STORAGE_KEYS } from '../utils/constants';

export const CartContext = createContext();

/**
 * Custom hook to use cart context
 */
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

/**
 * Load cart from localStorage
 */
const loadCartFromStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.CART_ITEMS);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading cart from storage:', error);
    return [];
  }
};

/**
 * Save cart to localStorage
 */
const saveCartToStorage = (items) => {
  try {
    localStorage.setItem(STORAGE_KEYS.CART_ITEMS, JSON.stringify(items));
  } catch (error) {
    console.error('Error saving cart to storage:', error);
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(loadCartFromStorage);

  // Sync to localStorage whenever items change
  useEffect(() => {
    saveCartToStorage(items);
  }, [items]);

  /**
   * Add a product to cart or increment quantity if already exists
   * @param {Object} product - Product object with id, name, price, images
   * @param {number} qty - Quantity to add
   */
  const addToCart = useCallback((product, qty = 1) => {
    setItems((prevItems) => {
      const existingIndex = prevItems.findIndex((item) => item.productId === product.id);
      const maxQuantity = product.quantity;

      if (existingIndex >= 0) {
        // Update existing item quantity
        const currentQty = prevItems[existingIndex].quantity;
        const newQty = currentQty + qty;

        // Check if exceeding stock
        if (newQty > maxQuantity) {
          // You might want to return here or clamp. 
          // For now, let's just not update if it exceeds.
          // Or we can just return previous items without change.
          return prevItems;
        }

        const updated = [...prevItems];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: newQty,
        };
        return updated;
      }

      // Check if new item quantity exceeds stock
      if (qty > maxQuantity) {
        return prevItems;
      }

      // Add new item
      const newItem = {
        productId: product.id,
        nameEn: product.nameEn,
        nameAr: product.nameAr,
        price: product.price,
        quantity: qty,
        image: product.images?.[0] || null,
        maxQuantity: product.quantity, // Stock limit
      };
      return [...prevItems, newItem];
    });
  }, []);

  /**
   * Remove a product from cart
   * @param {number} productId - Product ID to remove
   */
  const removeFromCart = useCallback((productId) => {
    setItems((prevItems) => prevItems.filter((item) => item.productId !== productId));
  }, []);

  /**
   * Update quantity of a specific item
   * @param {number} productId - Product ID
   * @param {number} qty - New quantity (if 0 or less, item is removed)
   */
  const updateQuantity = useCallback((productId, qty) => {
    if (qty <= 0) {
      removeFromCart(productId);
      return;
    }

    setItems((prevItems) =>
      prevItems.map((item) =>
        item.productId === productId
          ? { ...item, quantity: qty }
          : item
      )
    );
  }, [removeFromCart]);

  /**
   * Clear all items from cart
   */
  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  /**
   * Get total price of all items
   */
  const getCartTotal = useCallback(() => {
    return items.reduce((total, item) => total + (item.price * item.quantity), 0);
  }, [items]);

  /**
   * Get total count of items
   */
  const getItemCount = useCallback(() => {
    return items.reduce((count, item) => count + item.quantity, 0);
  }, [items]);

  const value = useMemo(
    () => ({
      items,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getCartTotal,
      getItemCount,
    }),
    [items, addToCart, removeFromCart, updateQuantity, clearCart, getCartTotal, getItemCount]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
